import { useState, useEffect } from 'react';
import type { ConnectedSource } from '../../types/platforms';
import { useAuditStore } from '../../stores/useAuditStore';
import { deleteCredentials, getTikTokAuthUrl, startGAOAuth, startGoogleAdsOAuth, startLinkedInOAuth, startMetaOAuth } from '../../api/client';
import { PLATFORM_DESCRIPTIONS, PLATFORM_TO_PROVIDER } from '../../data/platforms';
import { Button } from '@/components/ui/button';
import { Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import CredentialModal from './CredentialModal';
import AppsFlyerCredentialModal from './AppsFlyerCredentialModal';
import ShopifyConnectModal from './ShopifyConnectModal';
import AppleSearchAdsCredentialModal from './AppleSearchAdsCredentialModal';
import { useActivePlatforms } from './activePlatforms';
import '../onboarding/onboarding.css';

// Platforms that use OAuth redirect flow instead of manual token entry
const OAUTH_PLATFORMS = new Set(['tiktok-ads']);

// Which platforms are connectable is runtime state now (Shopify is gated by the
// backend's config), so the former ACTIVE_PLATFORMS constant is a hook — see
// ./activePlatforms. An inactive platform keeps its card: dimmed, "Coming soon",
// Connect disabled.
//
// Shopify's "Connect"/"Manage" button opens ShopifyConnectModal, which lists
// connected stores and launches the App-Store-initiated install (the merchant
// installs from the listing; the backend parks the store, then redirects to
// /connect?shopify=claim where ConnectView claims it onto the account).

interface PlatformCardProps {
  platform: ConnectedSource;
}

export default function PlatformCard({ platform }: PlatformCardProps) {
  const { connectPlatform, disconnectPlatform } = useAuditStore();
  const [connecting, setConnecting] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const isActive = useActivePlatforms().has(platform.id);
  // 🔴 `&& isActive` DIVERGES from the e7ff810 precedent on purpose: that one
  // gated the disconnected arm only, and the ?shopify=claim flow we deliberately
  // keep alive is exactly what produces a connected state — so a reviewer
  // installing from the App Store listing would otherwise land on a fully live
  // card (undimmed, no badge, Manage working) while the integration is meant to
  // be hidden. Rendering only: the store keeps the real status, so the claim
  // completes and the audit still counts the source.
  const isConnected = platform.status === 'connected' && isActive;

  useEffect(() => {
    if (isConnected) setConnecting(false);
  }, [isConnected]);

  const handleConnect = async () => {
    if (!isActive) return;

    if (OAUTH_PLATFORMS.has(platform.id)) {
      setConnecting(true);
      try {
        const { url } = await getTikTokAuthUrl();
        if (url) {
          window.location.href = url;
        }
      } catch {
        setConnecting(false);
        setShowModal(true);
      }
      return;
    }

    if (platform.id === 'ga') {
      setConnecting(true);
      try {
        const { auth_url } = await startGAOAuth();
        window.location.href = auth_url;
      } catch {
        setConnecting(false);
      }
      return;
    }

    if (platform.id === 'google-ads') {
      setConnecting(true);
      try {
        const { auth_url } = await startGoogleAdsOAuth();
        window.location.href = auth_url;
      } catch {
        setConnecting(false);
      }
      return;
    }

    if (platform.id === 'meta-ads') {
      setConnecting(true);
      try {
        const { auth_url } = await startMetaOAuth();
        window.location.href = auth_url;
      } catch {
        setConnecting(false);
      }
      return;
    }

    if (platform.id === 'linkedin-ads') {
      setConnecting(true);
      try {
        const { auth_url } = await startLinkedInOAuth();
        window.location.href = auth_url;
      } catch {
        setConnecting(false);
      }
      return;
    }

    setShowModal(true);
  };

  const handleConnected = () => {
    setConnecting(true);
    connectPlatform(platform.id);
  };

  const handleDisconnect = async () => {
    const provider = PLATFORM_TO_PROVIDER[platform.id];
    if (provider) {
      try {
        await deleteCredentials(provider);
      } catch { /* best effort */ }
    }
    disconnectPlatform(platform.id);
  };

  // Shopify's connected state opens the store manager rather than
  // disconnecting outright — a merchant may have several stores linked.
  const managesInsteadOfDisconnecting = platform.id === 'shopify';

  return (
    <>
      <div className={cn(
        'flex items-center gap-2.5 rounded-[9px] border bg-card px-2.5 py-2 transition-[border-color,box-shadow] hover:border-muted-foreground/30 hover:shadow-sm',
        !isActive && !isConnected && 'opacity-60',
      )}>
        <div className={cn(
          'flex size-[26px] shrink-0 items-center justify-center overflow-hidden rounded-[7px] border text-[11px] font-bold',
          platform.logo ? 'bg-transparent' : 'bg-muted text-muted-foreground',
        )}>
          {platform.logo ? (
            <img src={platform.logo} alt={platform.name} className={cn('h-full w-full object-cover', platform.darkLogo && 'dark:invert')} />
          ) : (
            platform.iconLabel
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="text-[12.5px] font-bold text-foreground">{platform.name}</span>
            {!isActive && !isConnected && (
              <span className="rounded-full border px-1.5 py-0.5 text-[9px] font-medium leading-none text-muted-foreground">
                Coming soon
              </span>
            )}
          </div>
          <div className="truncate text-[11px] text-muted-foreground">
            {isConnected && platform.accountName
              ? platform.accountName
              : connecting
                ? 'Authenticating...'
                : (PLATFORM_DESCRIPTIONS[platform.id] || 'Not connected')}
          </div>
        </div>
        {isConnected ? (
          <button
            type="button"
            data-hover={managesInsteadOfDisconnecting ? 'Manage' : 'Disconnect'}
            onClick={managesInsteadOfDisconnecting ? () => setShowModal(true) : handleDisconnect}
            className={cn(
              'ob-toggle-connected inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-[7px] border px-3 py-1 text-[11.5px] font-semibold transition-colors',
              'border-success/25 bg-success/10 text-success',
              'hover:border-destructive/25 hover:bg-destructive/10 hover:text-destructive',
            )}
          >
            <Check className="size-3" strokeWidth={2.5} />
            <span>Connected</span>
          </button>
        ) : (
          <Button
            size="sm"
            variant={connecting ? 'ghost' : 'default'}
            onClick={handleConnect}
            disabled={connecting || !isActive}
            className="h-auto shrink-0 cursor-pointer rounded-[7px] px-3 py-1 text-[11.5px] font-semibold"
          >
            {connecting ? (
              <>
                <Loader2 className="size-3 animate-spin" />
                Connecting...
              </>
            ) : (
              'Connect'
            )}
          </Button>
        )}
      </div>
      {showModal && (
        platform.id === 'shopify' ? (
          <ShopifyConnectModal
            onClose={() => setShowModal(false)}
            onShopsChanged={(count) => {
              if (count > 0) connectPlatform('shopify');
              else disconnectPlatform('shopify');
            }}
          />
        ) : platform.id === 'apple-search-ads' ? (
          <AppleSearchAdsCredentialModal
            platformName={platform.name}
            onClose={() => setShowModal(false)}
            onConnected={handleConnected}
          />
        ) : platform.id === 'appsflyer' ? (
          <AppsFlyerCredentialModal
            platformName={platform.name}
            onClose={() => setShowModal(false)}
            onConnected={handleConnected}
          />
        ) : (
          <CredentialModal
            platformId={platform.id}
            platformName={platform.name}
            onClose={() => setShowModal(false)}
            onConnected={handleConnected}
          />
        )
      )}
    </>
  );
}
